/**
 * API отправки SMS клиенту через edge function `send-sms`.
 *
 * Бэкенд (репо `stryzh`): `supabase/functions/send-sms/index.ts` — шлюз к
 * SMS Traffic. Принимает Bearer JWT админа/диспетчера, шлёт одиночный SMS
 * через api.smstraffic.ru с fallback на api2 при code=1000.
 *
 * Логин/пароль/имя отправителя — в env на сервере (SMSTRAFFIC_LOGIN,
 * SMSTRAFFIC_PASSWORD, SMSTRAFFIC_ORIGINATOR; аккаунт общий с проектом
 * Carlink). На фронт креды не попадают.
 */
import { createClient } from '@/api/supabase'

export interface SendSmsParams {
  /** Любой формат: «+7 999 …», «8 …», «79991234567». Нормализуется на бэке. */
  phone: string
  /** UTF-8 текст SMS. Длина не валидируется — диспетчер видит превью на форме. */
  message: string
  /** Лид, по которому отправляется SMS. Уйдёт в `sms_outbox.lead_id` для
   *  раздела «Коммуникации» в карточке. NULL допустим — лог запишется без
   *  связи, и SMS попадёт в раздел по phone. */
  leadId?: string | null
  /** Источник отправки. 'manual' — ручная кнопка диспетчера; 'awaiting_payment' /
   *  'paid' / 'report' — триггеры на лиде; 'other' — fallback. */
  source?: 'manual' | 'awaiting_payment' | 'paid' | 'report' | 'other'
}

export type SendSmsResult =
  | {
      ok: true
      /** Код SMS Traffic (0 = queued). */
      code: number
      /** «queued 1 messages» при успехе. */
      description: string
      /** Какие хосты пробовались: ["api"] либо ["api", "api2"]. */
      attemptedHosts: string[]
    }
  | {
      ok: false
      /** Прикладной код SMS Traffic (401, 411, 415, 436, 1000, …). null — сетевая ошибка. */
      code: number | null
      /** Сообщение от SMS Traffic либо причина сетевой ошибки. */
      description: string
      attemptedHosts?: string[]
      /** Категория ошибки, выставляется при HTTP != 2xx. */
      reason?:
        | 'missing_authorization'
        | 'invalid_session'
        | 'forbidden'
        | 'invalid_json'
        | 'missing_fields'
        | 'invalid_phone'
        | 'missing_secrets'
        | 'smstraffic_unreachable'
        | 'invoke_failed'
    }

/**
 * Шлёт SMS через edge function. Возвращает структурированный результат
 * независимо от того, ошибка ли это сети, прикладная ошибка SMS Traffic
 * или успех — вызывающий код решает, как показать.
 */
export async function sendSmsToLead(
  params: SendSmsParams,
): Promise<SendSmsResult> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('send-sms', {
    body: {
      phone: params.phone,
      message: params.message,
      lead_id: params.leadId ?? null,
      source: params.source ?? 'manual',
    },
  })

  if (error) {
    // supabase-js при HTTP != 2xx упаковывает тело ответа в error.context.
    // Пытаемся достать структурированный reason из тела.
    const ctx = (error as { context?: Response }).context
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as SendSmsResult
        return parsed
      } catch {
        // fallthrough — отдадим обобщённую ошибку
      }
    }
    return {
      ok: false,
      code: null,
      description: error.message,
      reason: 'invoke_failed',
    }
  }

  return (data ?? {
    ok: false,
    code: null,
    description: 'no_response',
    reason: 'invoke_failed',
  }) as SendSmsResult
}

// ===========================================================================
// SMS-шаблоны клиенту — единый источник с триггерами БД.
//
// Тексты всех 4 SMS клиенту хранятся в public.system_config (ключи
// sms_template_*, миграция 20260603120000). И авто-отправка (триггеры БД),
// и ручные кнопки админки читают ОДИН источник — текст не дрейфит. Редактор
// текстов — страница «Авито → SMS» (/sms-settings).
//
// В шаблоне плейсхолдер {link} — конкретный URL (/z/ форма или /s/ статус)
// подставляет билдер ниже, шаблон хранит только формулировку.
// ===========================================================================

export interface SmsTemplates {
  lead: string
  paid: string
  status: string
  report: string
  /** Триггер на orders.inspector_id NULL→NOT NULL (только для Avito-лидов).
   *  Миграция stryzh 20260620160000. */
  assigned: string
}

/** Дефолты — ДОЛЖНЫ совпадать с seed в миграциях 20260603120000
 *  (lead/paid/status/report) и 20260620160000 (assigned). Fallback,
 *  если ключа нет / значение пустое. */
export const SMS_TEMPLATE_DEFAULTS: SmsTemplates = {
  lead: 'Осмотр авто по заявке Авито {link}',
  paid: 'Осмотр оплачен. Статус: {link}',
  status: 'Статус осмотра: {link}',
  report: 'Отчёт об осмотре готов: {link}',
  assigned: 'Отслеживать статус осмотра: {link}',
}

export const SMS_TEMPLATE_KEYS: Record<keyof SmsTemplates, string> = {
  lead: 'sms_template_lead',
  paid: 'sms_template_paid',
  status: 'sms_template_status',
  report: 'sms_template_report',
  assigned: 'sms_template_assigned',
}

/** Читает 5 шаблонов из system_config. Пустые/отсутствующие → дефолт. */
export async function getSmsTemplates(): Promise<SmsTemplates> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('system_config')
    .select('key, value')
    .in('key', Object.values(SMS_TEMPLATE_KEYS))
  if (error) throw new Error(error.message)
  const byKey = new Map<string, string>(
    (data ?? []).map((r) => [(r as { key: string }).key, (r as { value: string }).value]),
  )
  const pick = (k: keyof SmsTemplates): string => {
    const v = (byKey.get(SMS_TEMPLATE_KEYS[k]) ?? '').trim()
    return v || SMS_TEMPLATE_DEFAULTS[k]
  }
  return {
    lead: pick('lead'),
    paid: pick('paid'),
    status: pick('status'),
    report: pick('report'),
    assigned: pick('assigned'),
  }
}

/** Подставляет {link}; пустой шаблон → fallback. split/join вместо replaceAll
 *  ради совместимости с целевым ES. */
function renderSms(template: string | undefined, fallback: string, link: string): string {
  const t = (template ?? '').trim() || fallback
  return t.split('{link}').join(link)
}

/** Текст SMS со ссылкой на форму заявки (/z/). `template` — из getSmsTemplates;
 *  без него используется дефолт. */
export function buildLeadSmsText(landingToken: string, template?: string): string {
  return renderSms(template, SMS_TEMPLATE_DEFAULTS.lead, `https://avs.carlink.ru/z/${landingToken}`)
}

/** Текст SMS со ссылкой на статус-страницу заказа (/s/). */
export function buildStatusSmsText(landingToken: string, template?: string): string {
  return renderSms(template, SMS_TEMPLATE_DEFAULTS.status, `https://avs.carlink.ru/s/${landingToken}`)
}

/** Текст SMS «отчёт готов» со ссылкой на статус-страницу (/s/ показывает
 *  блок отчёта). */
export function buildReportSmsText(landingToken: string, template?: string): string {
  return renderSms(template, SMS_TEMPLATE_DEFAULTS.report, `https://avs.carlink.ru/s/${landingToken}`)
}
